import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { ArrowRight, UserCheck, WifiOff } from "lucide-react";
import { sessionApi, type Destination } from "../../api/account";
import { ApiError } from "../../api/client";
import { AuthHeading, AuthLayout, BasketNote, FormError, Notice, Repeated, SocialButtons, TextButton } from "../../components/account/AuthLayout";
import { EmailField, emailProblem } from "../../components/account/EmailField";
import { Button, PhoneField, formatPH, isMobilePH } from "../../components/ui";
import { useCart } from "../cart/useCart";
import { usePageTitle } from "../catalogue/usePageTitle";
import { CodeSteps } from "./CodeSteps";
import { fieldError, useReturnTo } from "./session";
import { useSocialSession, useStore } from "./social";

export type Channel = "phone" | "email";

/** What the first screen of registering or resetting starts with, e.g. what was typed at sign-in. */
export type Prefill = { channel?: Channel; phone?: string; email?: string };

const PHONE_FORMAT = "Mobile numbers have 10 digits after +63, starting with 9, like 917 123 4567";

/** The first screen's field, checked before asking the server. */
export function destinationProblem(channel: Channel, phone: string, email: string): string | undefined {
  if (channel === "email") return emailProblem(email);
  if (!phone) return "Enter your mobile number";
  if (!isMobilePH(phone)) return PHONE_FORMAT;
}

export function destinationFor(channel: Channel, phone: string, email: string): { destination: Destination; shown: string } {
  return channel === "phone"
    ? { destination: { phone_number: phone }, shown: `+63 ${formatPH(phone)}` }
    : { destination: { email_address: email.trim().toLowerCase() }, shown: email.trim().toLowerCase() };
}

/**
 * PRD screen 13: register with a mobile number or an email, which gets a code first (then
 * CodeSteps), or with Google or Facebook. Phone is offered only where the server can send
 * codes; elsewhere the screen opens on email.
 */
export function RegisterRoute() {
  const returnTo = useReturnTo();
  const prefill: Prefill = (useLocation().state as { prefill?: Prefill } | null)?.prefill ?? {};
  const store = useStore();
  const cart = useCart();
  const social = useSocialSession(returnTo);
  const phoneOffered = store.data ? store.data.sign_in.phone : true;
  const [chosen, setChosen] = useState<Channel>(prefill.channel ?? "phone");
  const channel: Channel = phoneOffered ? chosen : "email";
  const [phone, setPhone] = useState(prefill.phone ?? "");
  const [email, setEmail] = useState(prefill.email ?? "");
  const [problem, setProblem] = useState<string>();
  const [sent, setSent] = useState<{ resendAfter: number } | null>(null);
  const { destination, shown } = destinationFor(channel, phone, email);
  usePageTitle("Create an account");

  const send = useMutation({
    mutationFn: () => sessionApi.sendSignUpCode(destination),
    onSuccess: (answer) => setSent({ resendAfter: answer.resend_after }),
    onError: (error) => {
      // A code went out a moment ago: it still works, so carry on to the code screen.
      if (error instanceof ApiError && error.code === "code_recently_sent") setSent({ resendAfter: Number(error.details.retry_after?.[0] ?? 60) });
      if (error instanceof ApiError && error.code === "phone_unavailable") {
        setChosen("email");
        setProblem(undefined);
      }
    },
  });

  if (sent) {
    return (
      <AuthLayout back={returnTo}>
        <CodeSteps
          purpose="sign_up"
          destination={destination}
          shown={shown}
          resendAfter={sent.resendAfter}
          onChangeDestination={() => {
            send.reset();
            setSent(null);
          }}
          returnTo={returnTo}
          cartCount={cart.data?.item_count ?? 0}
        />
      </AuthLayout>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = destinationProblem(channel, phone, email);
    setProblem(next);
    if (!next) send.mutate();
  };

  const switchChannel = () => {
    setChosen(channel === "phone" ? "email" : "phone");
    setProblem(undefined);
    send.reset();
  };

  const code = send.error instanceof ApiError ? send.error.code : null;
  const exists = code === "phone_taken" || code === "email_taken";
  const serverField = fieldError(send.error, channel === "phone" ? "phone_number" : "email_address");
  // Anything but an answer about the number or address: the code didn't go.
  const sendFailed = send.isError && !exists && !serverField && code !== "code_recently_sent" && code !== "phone_unavailable";

  return (
    <AuthLayout back={returnTo}>
      <AuthHeading title="Create an account">
        {channel === "phone" ? "Use your mobile number. We'll text you a code to check it." : "Use your email address. We'll send you a code to check it."}
      </AuthHeading>
      <BasketNote count={cart.data?.item_count ?? 0} />
      <form onSubmit={submit} noValidate>
        {channel === "phone" ? (
          <PhoneField
            value={phone}
            error={problem ?? serverField}
            disabled={send.isPending}
            onChange={(digits) => {
              setPhone(digits);
              setProblem(undefined);
              send.reset();
            }}
            autoFocus
          />
        ) : (
          <EmailField
            value={email}
            error={problem ?? serverField}
            disabled={send.isPending}
            onChange={(value) => {
              setEmail(value);
              setProblem(undefined);
              send.reset();
            }}
            autoFocus
          />
        )}
        {exists && (
          <Notice icon={UserCheck}>
            <Repeated>{shown}</Repeated> already has an account.{" "}
            <Link to="/sign-in" state={{ from: returnTo, login: channel === "phone" ? `0${phone}` : email }} className="font-semibold">
              Sign in
            </Link>
          </Notice>
        )}
        {sendFailed && (
          <Notice tone="danger" icon={WifiOff}>
            We couldn't send the code to <Repeated>{shown}</Repeated>. {channel === "phone" ? "Check the number and your signal" : "Check your connection"}, then try again.
          </Notice>
        )}
        <Button type="submit" fullWidth size="lg" iconRight={send.isPending ? undefined : ArrowRight} disabled={send.isPending || social.busy !== null} className="mt-6">
          {send.isPending ? "Sending your code…" : sendFailed ? "Try again" : "Next"}
        </Button>
      </form>
      {phoneOffered && (
        <div className="mt-5">
          <TextButton onClick={switchChannel}>{channel === "phone" ? "Sign up with email instead" : "Sign up with a mobile number instead"}</TextButton>
        </div>
      )}

      <SocialButtons available={social.available} busy={social.busy} onChoose={social.choose} />
      <FormError>{social.error}</FormError>

      <p className="mt-7 mb-0 type-small text-text-muted">
        Already have an account?{" "}
        <Link to="/sign-in" state={{ from: returnTo }} className="font-semibold">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
